import { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Star, Quote } from "lucide-react";

// Placeholder testimonials - replace with real Google reviews when available
const testimonials = [
  {
    name: "Michael S.",
    rating: 5,
    text: "Super Arbeit! Mein Auto sieht nach dem Unfallschaden wieder aus wie neu. Sehr professionell und schnelle Abwicklung mit der Versicherung.",
    date: "vor 2 Wochen",
    vehicle: "BMW 3er"
  },
  {
    name: "Sandra K.",
    rating: 5,
    text: "Kann ich nur empfehlen! Freundliches Team, faire Preise und die Lackierung ist perfekt geworden. Farbton stimmt 1:1.",
    date: "vor 1 Monat",
    vehicle: "VW Golf"
  },
  {
    name: "Thomas M.",
    rating: 5,
    text: "Schon zum zweiten Mal hier gewesen. Spot-Repair vor der Leasingrückgabe hat mir viel Geld gespart. Top Service!",
    date: "vor 3 Wochen",
    vehicle: "Audi A4"
  },
  {
    name: "Petra W.",
    rating: 5,
    text: "Sehr kompetente Beratung und transparente Preise. Die Felgenaufbereitung ist erstklassig. Vielen Dank!",
    date: "vor 1 Monat",
    vehicle: "Mercedes C-Klasse"
  }
];

const StarRating = ({ rating }) => {
  return (
    <div className="flex gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${
            i < rating ? "text-yellow-400 fill-yellow-400" : "text-muted-foreground/30"
          }`}
        />
      ))}
    </div>
  );
};

export const Testimonials = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const averageRating = (testimonials.reduce((sum, t) => sum + t.rating, 0) / testimonials.length).toFixed(1);

  return (
    <section
      ref={sectionRef}
      className="py-16 lg:py-24 bg-secondary/30"
    >
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <span className="inline-block text-sm font-semibold text-primary uppercase tracking-wider mb-3">
            Kundenstimmen
          </span>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Das sagen unsere Kunden
          </h2>
          
          {/* Overall Rating */}
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="flex items-center gap-1">
              <Star className="h-6 w-6 text-yellow-400 fill-yellow-400" />
              <span className="text-2xl font-bold text-foreground">{averageRating}</span>
            </div>
            <span className="text-muted-foreground">
              basierend auf {testimonials.length}+ Bewertungen
            </span>
          </div>
          
          <a
            href="https://www.google.com/maps/search/Lack+Zentrum+Wilke+Magdeburg"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-primary hover:underline text-sm"
          >
            <img 
              src="https://www.google.com/favicon.ico" 
              alt="Google" 
              className="h-4 w-4"
            />
            Alle Bewertungen auf Google ansehen
          </a>
        </div>

        {/* Testimonials Grid */}
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 ${
          isVisible ? 'stagger-children' : ''
        }`}>
          {testimonials.map((testimonial, index) => (
            <Card
              key={index}
              className="bg-card border border-border/50 hover:border-primary/20 transition-colors duration-300"
            >
              <CardContent className="p-6">
                {/* Quote Icon */}
                <Quote className="h-8 w-8 text-primary/20 mb-4" />
                
                {/* Rating */}
                <StarRating rating={testimonial.rating} />
                
                {/* Text */}
                <p className="text-muted-foreground text-sm leading-relaxed mt-4 mb-4">
                  "{testimonial.text}"
                </p>
                
                {/* Author */}
                <div className="border-t border-border pt-4">
                  <p className="font-semibold text-foreground text-sm">
                    {testimonial.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {testimonial.vehicle} • {testimonial.date}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
